/**
 * `<CreateApiKeyDialog>` — collects name + expiry, calls
 * `authClient.createApiKey`, and on success hands the raw token back to
 * the parent via `onCreated`. The parent (`<ApiKeysSettings>`) renders
 * `<NewKeyDisplayDialog>` once with that raw token; closing it drops
 * the token from React state so it can't be re-displayed.
 *
 * Expiry shortcuts (`30d`, `90d`, `1y`) are resolved to ISO timestamps
 * here. "Never" sends `null`. The shared `createApiKeySchema` validates
 * after this resolution.
 */

import { authClient, type AuthClientError } from "@cometchat-ai/auth/client";
import type { ApiKeyWithToken } from "@cometchat-ai/auth/client";
import { createApiKeySchema, type CreateApiKeyDto } from "@cometchat-ai/auth/shared";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2, X } from "./icons";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { formatApiError } from "../lib/format-error";

type ExpiryShortcut = "never" | "30d" | "90d" | "1y";

const DAY_MS = 24 * 60 * 60 * 1000;

function shortcutToIso(shortcut: ExpiryShortcut): string | null {
  if (shortcut === "never") return null;
  const days = shortcut === "30d" ? 30 : shortcut === "90d" ? 90 : 365;
  return new Date(Date.now() + days * DAY_MS).toISOString();
}

export interface CreateApiKeyDialogProps {
  workspaceSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (key: ApiKeyWithToken) => void;
}

export function CreateApiKeyDialog({
  workspaceSlug,
  open,
  onOpenChange,
  onCreated,
}: CreateApiKeyDialogProps) {
  const { t } = useTranslation("apikeys");
  const queryClient = useQueryClient();
  const [expiry, setExpiry] = useState<ExpiryShortcut>("never");

  const form = useForm<CreateApiKeyDto>({
    resolver: zodResolver(createApiKeySchema),
    defaultValues: { name: "", expiresAt: null },
  });

  useEffect(() => {
    if (open) {
      form.reset({ name: "", expiresAt: null });
      setExpiry("never");
    }
  }, [open, form]);

  const mutation = useMutation({
    mutationFn: (values: CreateApiKeyDto) => authClient.createApiKey(workspaceSlug, values),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["api-keys", workspaceSlug] });
      onCreated(created);
      onOpenChange(false);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("createDialog.failed"), { description: formatApiError(err) });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md gap-0 rounded-3xl p-0 sm:rounded-3xl"
        data-testid="create-api-key-dialog"
      >
        <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
          <DialogTitle className="text-base font-medium tracking-[0.01em]">
            {t("createDialog.title")}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-muted-foreground">
            {t("createDialog.subtitle")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) =>
              mutation.mutate({ name: values.name, expiresAt: shortcutToIso(expiry) }),
            )}
            data-testid="create-api-key-form"
            noValidate
          >
            <div className="space-y-4 px-5 pb-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-[13px] font-medium text-foreground">
                      {t("createDialog.name.label")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("createDialog.name.placeholder")}
                        className="h-9 rounded-lg text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                        data-testid="create-api-key-name"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs text-muted-foreground">
                      {t("createDialog.name.hint")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-1.5">
                <Label
                  htmlFor="create-api-key-expiry"
                  className="text-[13px] font-medium text-foreground"
                >
                  {t("createDialog.expiry.label")}
                </Label>
                <Select
                  value={expiry}
                  onValueChange={(value) => setExpiry(value as ExpiryShortcut)}
                >
                  <SelectTrigger
                    id="create-api-key-expiry"
                    className="h-9 rounded-lg text-[13px]"
                    data-testid="create-api-key-expiry"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">{t("createDialog.expiry.options.never")}</SelectItem>
                    <SelectItem value="30d">{t("createDialog.expiry.options.30d")}</SelectItem>
                    <SelectItem value="90d">{t("createDialog.expiry.options.90d")}</SelectItem>
                    <SelectItem value="1y">{t("createDialog.expiry.options.1y")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="flex-row gap-2 px-5 pb-5 pt-3">
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={mutation.isPending}
                  className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
                  data-testid="create-api-key-cancel"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("createDialog.cancel")}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={mutation.isPending}
                className="h-8 flex-1 gap-1.5 rounded-md text-[13px]"
                data-testid="create-api-key-submit"
              >
                {mutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {mutation.isPending ? t("createDialog.submitting") : t("createDialog.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
